import type { PrimaryTransferViews } from '../worker/protocol';
import { CLIMATE_RENDER_STRIDE } from '../worker/climate-protocol';
import { VEGETATION_VALUE_COUNT } from '../simulation/vegetation-field';
import {
  CELESTIAL_ENTITY_COUNT,
  CELESTIAL_BODY_SPECS,
} from '../simulation/celestial-system';

export class RendererState {
  public readonly planetaryClimate = new Float32Array(CELESTIAL_BODY_SPECS.length * CLIMATE_RENDER_STRIDE);
  public count = 0;
  public celestialCount = 0;
  public previousCount = 0;
  public latestStateReceivedAt = 0;
  public previousSimulatedSeconds = 0;
  public currentSimulatedSeconds = 0;
  public interpolationDurationSeconds = 1 / 30;
  public readonly previousX: Float32Array;
  public readonly previousY: Float32Array;
  public readonly currentX: Float32Array;
  public readonly currentY: Float32Array;
  public readonly mass: Float32Array;
  public readonly kind: Float32Array;
  public readonly planetaryAlgaeCoverage = new Float32Array(
    CELESTIAL_BODY_SPECS.length,
  );
  public readonly planetaryVegetation = new Float32Array(
    VEGETATION_VALUE_COUNT,
  );
  private readonly currentLineage: Uint32Array;
  private readonly previousIndexByLineage = new Map<number, number>();
  private readonly lastX: Float32Array;
  private readonly lastY: Float32Array;
  private hasState = false;

  public constructor(public readonly capacity: number) {
    this.previousX = new Float32Array(capacity);
    this.previousY = new Float32Array(capacity);
    this.currentX = new Float32Array(capacity);
    this.currentY = new Float32Array(capacity);
    this.mass = new Float32Array(capacity);
    this.kind = new Float32Array(capacity);
    this.currentLineage = new Uint32Array(capacity);
    this.lastX = new Float32Array(capacity);
    this.lastY = new Float32Array(capacity);
  }

  /** The next snapshot starts a new interpolation history after a gap/reset. */
  public resetInterpolation(): void {
    this.hasState = false;
    this.previousIndexByLineage.clear();
  }

  public accept(
    views: PrimaryTransferViews,
    count: number,
    simulatedSeconds: number,
    receivedAt: number,
    planetaryAlgaeCoverage: readonly number[] = [],
    planetaryVegetation?: Float32Array,
    wallIntervalSeconds = 1 / 30,
    celestialCount = CELESTIAL_ENTITY_COUNT,
    planetaryClimate?: Float32Array,
  ): void {
    const safeCount = Math.min(this.capacity, Math.max(0, count));
    this.celestialCount = Math.min(
      safeCount,
      Math.max(0, Math.floor(celestialCount)),
    );
    this.previousCount = this.count;
    const restarted =
      this.hasState && simulatedSeconds < this.currentSimulatedSeconds;

    if (this.hasState && !restarted) {
      this.lastX.set(this.currentX.subarray(0, this.count));
      this.lastY.set(this.currentY.subarray(0, this.count));
      this.previousIndexByLineage.clear();
      for (let index = 0; index < this.count; index += 1) {
        const lineage = this.currentLineage[index] ?? 0;
        if (lineage !== 0) {
          this.previousIndexByLineage.set(lineage, index);
        }
      }
    }

    this.mass.set(views.mass.subarray(0, safeCount));
    if (planetaryClimate !== undefined) this.planetaryClimate.set(planetaryClimate);
    this.planetaryAlgaeCoverage.set(planetaryAlgaeCoverage);
    if (planetaryVegetation !== undefined) {
      this.planetaryVegetation.set(planetaryVegetation);
    }
    for (let index = 0; index < safeCount; index += 1) {
      const currentX = views.x[index] ?? 0;
      const currentY = views.y[index] ?? 0;
      const lineage = views.lineage[index] ?? 0;
      const previousIndex =
        this.hasState && !restarted && lineage !== 0
          ? this.previousIndexByLineage.get(lineage)
          : undefined;
      this.previousX[index] =
        previousIndex === undefined
          ? currentX
          : (this.lastX[previousIndex] ?? currentX);
      this.previousY[index] =
        previousIndex === undefined
          ? currentY
          : (this.lastY[previousIndex] ?? currentY);
      this.currentX[index] = currentX;
      this.currentY[index] = currentY;
      this.currentLineage[index] = lineage;
      this.kind[index] = views.kind[index] ?? 0;
    }
    if (!this.hasState) {
      this.previousCount = safeCount;
      this.previousSimulatedSeconds = simulatedSeconds;
      this.hasState = true;
    } else if (restarted) {
      this.previousSimulatedSeconds = simulatedSeconds;
    } else {
      this.previousSimulatedSeconds = this.currentSimulatedSeconds;
    }
    this.currentSimulatedSeconds = simulatedSeconds;
    this.count = safeCount;
    this.latestStateReceivedAt = receivedAt;
    this.interpolationDurationSeconds = Math.max(
      1 / 30,
      Math.min(2, wallIntervalSeconds),
    );
  }

  public interpolation(now: number, enabled: boolean): number {
    if (!enabled || !this.hasState) {
      return 1;
    }
    return Math.max(
      0,
      Math.min(
        1,
        (now - this.latestStateReceivedAt) /
          (this.interpolationDurationSeconds * 1000),
      ),
    );
  }
}
